import { collection, addDoc, serverTimestamp } from 'firebase/firestore';
import { db, auth } from '../firebase.js';

// ─────────────────────────────────────────────────────────────────────────
// Centralized client-side error logging.
//
// Why this needs to exist separately from the Cloud Function logs
// (`firebase functions:log`): server-side code (generateHandler.js,
// askAI.js) already logs its own failures there — entitlement/rate-limit
// blocks, moderation flags, model call failures, etc. But a whole class of
// real-world failures NEVER reaches the server at all: a client-side fetch
// abort (a timeout firing, a tab backgrounded/closed mid-request, a dropped
// connection), a thrown error in the SSE-parsing loop, or anything else
// that fails before/without a clean server response. Those currently just
// show up as a one-off toast/inline message to whichever single user hit
// them, then vanish — there was no way to see "how often is this
// happening, to whom, on what" in aggregate.
//
// This writes one document per failure to the top-level `errorLogs`
// collection (see firestore.rules: any signed-in user may CREATE their own
// entry, but nobody — including that same user — can read, update, or
// delete any entry via the client SDK; the intent is a write-only mailbox
// the operator reads from the Firebase console / Admin SDK / an exported
// BigQuery sink, not something the app itself ever displays back). Logging
// is always best-effort: a failure here is swallowed, never surfaced to the
// user and never allowed to mask or replace the original error.
//
// TECHNICAL vs DISPLAY message — deliberately kept separate: several places
// upstream (see lib/generate.js) rewrite a raw browser/network error into a
// short, friendly string before it ever reaches the UI (e.g. a raw
// AbortError becomes "Generation timed out. Try Standard or Efficient...").
// That's the right call for what a learner sees, but it would throw away
// exactly the detail useful for mass-level debugging. So callers here pass
// the actual caught `error` object, and this logs its real `name`/`stack`/
// original message (plus anything upstream tagged onto it — `.stage`,
// `.technicalName`, `.technicalMessage`, `.httpStatus` — see
// lib/generate.js) IN ADDITION TO whatever friendly text the user saw. The
// friendly text never replaces the technical detail here.
// ─────────────────────────────────────────────────────────────────────────

/**
 * @param {object} opts
 * @param {string} opts.context - short machine-readable label for where this
 *   happened, e.g. 'generate', 'askAI'.
 * @param {Error} [opts.error] - the actual caught error/exception, if any.
 *   Its real name/message/stack are logged verbatim, along with any of the
 *   optional tags upstream code may have attached (see lib/generate.js):
 *   `.stage` (which specific step failed — 'getIdToken'|'fetch'|
 *   'stream-read'|'http'|'server'), `.technicalName`/`.technicalMessage`
 *   (the original error, if this one was already rewritten into something
 *   friendlier before being thrown), `.httpStatus`, `.rawHtml` (only
 *   presence is logged, never the content itself).
 * @param {string} [opts.displayMessage] - what the user actually saw on
 *   screen, if different from error.message (e.g. the
 *   "⚠️ Generation failed: ..." bubble text) — kept purely for
 *   cross-reference, never as a substitute for the technical fields above.
 * @param {string} [opts.courseId]
 * @param {string} [opts.topicId]
 * @param {object} [opts.extra] - any additional small, non-sensitive context
 *   (model profile, topic type, etc.) — never the prompt text or generated
 *   HTML itself, same "don't log user content" principle as entitlement.js's
 *   usage metering.
 */
export async function logClientError({ context, error, displayMessage, courseId, topicId, extra }) {
  try {
    const uid = auth.currentUser?.uid || null;
    await addDoc(collection(db, 'errorLogs'), {
      uid,
      context: String(context || 'unknown'),
      // The exact technical reason — never simplified/rewritten.
      errorName: error?.name ? String(error.name) : null,
      errorMessage: error?.message ? String(error.message).slice(0, 4000) : null,
      errorStack: error?.stack ? String(error.stack).slice(0, 4000) : null,
      // If upstream code rewrote a technical error into a friendlier one
      // before throwing it (see lib/generate.js), the ORIGINAL is preserved
      // here under these tags rather than lost.
      technicalName: error?.technicalName ? String(error.technicalName) : null,
      technicalMessage: error?.technicalMessage ? String(error.technicalMessage).slice(0, 4000) : null,
      stage: error?.stage ? String(error.stage) : null,
      httpStatus: typeof error?.httpStatus === 'number' ? error.httpStatus : null,
      hadRawHtml: !!error?.rawHtml,
      // What the user actually saw, purely for cross-reference.
      displayMessage: displayMessage ? String(displayMessage).slice(0, 2000) : null,
      courseId: courseId || null,
      topicId: topicId || null,
      // Firestore rejects `undefined` anywhere in a document, including
      // nested fields. Keep logging robust when optional UI metadata is
      // absent (for example, older topics without a `type`).
      extra: sanitizeForFirestore(extra),
      userAgent: typeof navigator !== 'undefined' ? navigator.userAgent : null,
      url: typeof location !== 'undefined' ? location.href : null,
      createdAt: serverTimestamp(),
    });
    return true;
  } catch (loggingErr) {
    // Never let telemetry failure be louder than the thing it's logging.
    console.warn('[errorLog] could not record error log entry:', loggingErr);
    return false;
  }
}

function sanitizeForFirestore(value) {
  if (value === undefined) return null;
  if (value === null) return null;
  if (Array.isArray(value)) return value.map(sanitizeForFirestore);
  if (typeof value !== 'object') return value;

  const cleaned = {};
  for (const [key, child] of Object.entries(value)) {
    cleaned[key] = sanitizeForFirestore(child);
  }
  return cleaned;
}
